import { EventEmitter, Injectable } from '@angular/core';
import { JobManagementModel, JobManagementService } from '../../origami-api/services/job-management.service';
import { NumberHash } from '../../shared/utils/utils-types';
import { BackgroundJob } from './background-job';
import { take } from 'rxjs/operators';
import { InactivityMonitorService } from '@app/session/inactivity-monitor.service';

@Injectable()
export class BackgroundJobsService {
  private jobs: BackgroundJob[] = [];
  private timeouts: NumberHash<any> = {};
  private timeout: number = 2000;

  change = new EventEmitter<BackgroundJob>();
  done = new EventEmitter<BackgroundJob>();
  error = new EventEmitter<any>();

  constructor(
    private readonly jobManagementService: JobManagementService,
    private readonly inactivityMonitorService: InactivityMonitorService,
  ) {}

  add(siteId: number, jobId: string, jobTitle: string, roomName?: string): BackgroundJob {
    const job = new BackgroundJob(siteId, jobId, jobTitle, roomName);
    this.jobs.push(job);
    this.listen(siteId);
    return job;
  }

  cancel(jobId: string): Promise<BackgroundJob | null> {
    return new Promise<BackgroundJob>((resolve) => {
      const job = this.jobs.find((item) => item.id === jobId);

      if (!job) {
        return resolve(null);
      }

      this.jobManagementService
        .cancelJob(job.siteId, job.id)
        .pipe(take(1))
        .subscribe(
          (res) => {
            this.updateJob(job, res.Result);
            resolve(job);
          },
          (e) => {
            this.error.emit(e);
            this.updateJob(job, null);
            resolve(job);
          },
        );
    });
  }

  private listen(siteId: number) {
    if (this.timeouts[siteId] || !this.jobs.some((item) => item.siteId === siteId)) {
      return;
    }

    this.timeouts[siteId] = setTimeout(() => {
      const jobIds = this.jobs.filter((item) => item.siteId === siteId).map((job) => job.id);
      if (!jobIds.length) {
        this.timeouts[siteId] = null;
        return;
      }

      this.jobManagementService
        .getJobsResult(siteId, jobIds)
        .pipe(take(1))
        .subscribe(
          (res) => {
            this.inactivityMonitorService.onActivity();
            this.updateJobsWithResults(siteId, res.Result);
            this.timeouts[siteId] = null;
            this.listen(siteId);
          },
          (e) => {
            this.error.emit(e);
            this.updateJobsWithResults(siteId, null);
            this.timeouts[siteId] = null;
          },
        );
    }, this.timeout);
  }

  private updateJobsWithResults(roomId: number, results: JobManagementModel[] | null) {
    this.jobs.forEach((job) => {
      if (job.siteId === roomId) {
        const jobResult = results ? results.find((jobRes) => jobRes.JobId === job.id) : null;
        this.updateJob(job, jobResult);
      }
    });
  }

  private updateJob(job: BackgroundJob, jobResult: JobManagementModel) {
    if (job) {
      job.update(jobResult);
      this.change.emit(job);

      if (job.completed) {
        this.removeJob(job.id);
        this.done.emit(job);
      }
    }
  }

  private removeJob(jobId: string) {
    for (let i = 0; i < this.jobs.length; i++) {
      if (this.jobs[i].id === jobId) {
        this.jobs.splice(i, 1);
      }
    }
  }
}
